import React, { useEffect, useRef, useState } from "react";
import { Link, useOutletContext, useSearchParams } from "react-router-dom";
import { Play, ArrowUpRight, Info } from "lucide-react";
import { toast } from "sonner";

import { projectApi, formatApiError } from "@/services/projectApi";
import { useTier } from "@/contexts/AuthContext";
import { LiveYieldPanel } from "@/components/yield/LiveYieldWidgets";

const SOLVER_TOOLTIP =
    "Phase 2B-ii runs a synthetic transient on a lumped-node Freiesleben-Hansen & Pedersen hydration model. Results illustrate the dashboard contract; Code_Aster integration is deferred and the solver interface is preserved for swap-in.";

const Mono = ({ children, className = "" }) => <span className={`tc-mono text-[12px] text-[#0e1416] ${className}`}>{children}</span>;
const Panel = ({ title, sub, children, testid }) => (
    <section data-testid={testid} className="border border-[#d8dcdd] bg-[#ffffff] mb-5">
        <header className="flex items-baseline justify-between px-5 py-4 border-b border-[#d8dcdd]">
            <h3 className="tc-mono text-[13px] tracking-wider uppercase text-[#0e1416]">{title}</h3>
            {sub && <span className="tc-mono text-[11px] text-[#5a6669]">{sub}</span>}
        </header>
        <div className="p-5">{children}</div>
    </section>
);

const PillarHeader = ({ index, title, intro, testid }) => (
    <div data-testid={testid} className="mt-1 mb-3 flex items-baseline gap-3 border-b border-[#d8dcdd] pb-2">
        <span className="tc-mono text-[11px] text-[#01696f] tracking-widest">PILLAR {index}</span>
        <h2 className="tc-mono text-[13px] tracking-wider uppercase text-[#0e1416]">· {title}</h2>
        {intro && <span className="tc-mono text-[11px] text-[#5a6669] ml-auto hidden md:inline">{intro}</span>}
    </div>
);

const IntroStrip = () => (
    <div data-testid="thermal-intro-strip" className="border border-[#d8dcdd] bg-[#f6f7f6] px-5 py-3 mb-5 flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="tc-mono text-[11px] text-[#5a6669] tracking-wider uppercase">Workflow</span>
        <span className="tc-mono text-[12.5px] text-[#0e1416]">Temperature-time history</span>
        <span className="tc-mono text-[12.5px] text-[#01696f]">→</span>
        <span className="tc-mono text-[12.5px] text-[#0e1416]">[ Thermal performance &amp; cracking risk ]</span>
        <span className="tc-mono text-[12.5px] text-[#01696f]">+</span>
        <span className="tc-mono text-[12.5px] text-[#0e1416]">[ Maturity → mix-calibrated strength ]</span>
    </div>
);

const SolverBadge = ({ overlayActive }) => (
    <span
        data-testid="solver-provenance-badge"
        title={overlayActive
            ? "Predicted lines come from the FHP synthetic solver. Monitored lines come from on-site probes. Concrete Control applies the same maturity engine and the same mix-specific calibration curve to both."
            : SOLVER_TOOLTIP}
        className="inline-flex items-center gap-1.5 tc-mono text-[10.5px] text-[#5a6669] border border-[#d8dcdd] bg-[#f6f7f6] px-2 py-1 cursor-help"
    >
        <Info size={11} strokeWidth={1.5} className="text-[#01696f]" />
        {overlayActive
            ? "solver: mock_fhp_v1 · synthetic transient (predicted) + measured (monitored)"
            : "solver: mock_fhp_v1 · synthetic transient"}
    </span>
);

const CIF_BANDS = [
    { test: (v) => v < 0.7, label: "Low cracking risk", tone: "ok", testid: "cif-band-low",
      body: "Predicted tensile demand stays well below capacity across the 72 h window — no thermal cracking expected for this configuration." },
    { test: (v) => v < 1.0, label: "Elevated cracking risk", tone: "warn", testid: "cif-band-elevated",
      body: "Predicted tensile demand approaches capacity within the 72 h window — review insulation, placement temperature, or binder blend to widen the margin." },
    { test: () => true, label: "Cracking predicted", tone: "fail", testid: "cif-band-fail",
      body: "Predicted tensile demand exceeds capacity within the 72 h window — mitigation is required (cooling pipes, lower placement temperature, or revised binder/SCM blend)." },
];
const toneClasses = { ok: "border-[#01696f] bg-[rgba(1,105,111,0.06)] text-[#01696f]", warn: "border-[#b3741f] bg-[rgba(179,116,31,0.08)] text-[#b3741f]", fail: "border-[#a13434] bg-[rgba(161,52,52,0.08)] text-[#a13434]" };

// ── Phase 2B-iii close-out · interpolation helpers used by Panels 2/3/6 ──
const interpolateProbeOntoGrid = (samples, grid) => {
    if (!samples || samples.length < 2) return grid.map(() => null);
    const sorted = [...samples].sort((a, b) => a.t_hours_from_placement - b.t_hours_from_placement);
    const tMin = sorted[0].t_hours_from_placement;
    const tMax = sorted[sorted.length - 1].t_hours_from_placement;
    return grid.map((tg) => {
        if (tg < tMin || tg > tMax) return null; // no extrapolation
        for (let i = 0; i < sorted.length - 1; i++) {
            const a = sorted[i], b = sorted[i + 1];
            if (a.t_hours_from_placement <= tg && tg <= b.t_hours_from_placement) {
                const span = Math.max(1e-9, b.t_hours_from_placement - a.t_hours_from_placement);
                const w = (tg - a.t_hours_from_placement) / span;
                return a.temperature_C + w * (b.temperature_C - a.temperature_C);
            }
        }
        return null;
    });
};
const meanIgnoringNull = (vals) => {
    const xs = vals.filter((v) => v != null);
    return xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null;
};
const probeBuckets = (monitoring) => {
    const out = { core: [], mid: [], surface: [], other: [] };
    if (!monitoring) return out;
    for (const b of monitoring.by_probe || []) {
        if (!b.probe || !(b.records || []).length) continue;
        const flatSamples = (b.records || []).flatMap((r) => (r.samples || []).map((s) => ({ ...s, _record: r })));
        if (!flatSamples.length) continue;
        const bucket = out[b.probe.location_hint] || out.other;
        bucket.push({ probe: b.probe, samples: flatSamples, records: b.records });
    }
    return out;
};
const computeMonitoredDeltaT = (grid, buckets) => {
    if (!buckets.core.length || !buckets.surface.length) return { series: [], peak: null };
    const coreSeries = buckets.core.map((p) => interpolateProbeOntoGrid(p.samples, grid));
    const surfSeries = buckets.surface.map((p) => interpolateProbeOntoGrid(p.samples, grid));
    const series = grid.map((tg, i) => {
        const c = meanIgnoringNull(coreSeries.map((s) => s[i]));
        const su = meanIgnoringNull(surfSeries.map((s) => s[i]));
        if (c == null || su == null) return null;
        return { t: tg, dT: c - su };
    });
    const valid = series.filter((p) => p != null);
    let peak = null;
    for (const p of valid) {
        if (!peak || p.dT > peak.dT) peak = p;
    }
    return { series, peak };
};
const computeMonitoredTe = (grid, buckets, method, params) => {
    if (!buckets.core.length) return { series: [], peak: null };
    const coreSeries = buckets.core.map((p) => interpolateProbeOntoGrid(p.samples, grid));
    const T_ref_K = (params?.T_ref ?? 20) + 273.15;
    const Ea = params?.E_a ?? 33500;
    const T0 = params?.datum_temp ?? -10;
    const R = 8.314;
    let teRun = 0; let prevT = null;
    const series = [];
    for (let i = 0; i < grid.length; i++) {
        const tg = grid[i];
        const T_mean = meanIgnoringNull(coreSeries.map((s) => s[i]));
        if (T_mean == null) { series.push(null); prevT = null; continue; }
        if (prevT == null || i === 0) { prevT = T_mean; series.push({ t: tg, te: teRun }); continue; }
        const dt_h = tg - grid[i - 1];
        const Tavg = 0.5 * (prevT + T_mean);
        const inc = method === "arrhenius"
            ? Math.exp(-(Ea / R) * (1 / (Tavg + 273.15) - 1 / T_ref_K)) * dt_h
            : Math.max(0, Tavg - T0) * dt_h;
        teRun += inc;
        series.push({ t: tg, te: teRun });
        prevT = T_mean;
    }
    const valid = series.filter((p) => p != null);
    const peak = valid.length ? valid[valid.length - 1] : null;
    return { series, peak };
};
const sourceMixLabel = (records) => {
    const sources = new Set();
    for (const r of records || []) sources.add(r.source);
    if (sources.size === 0) return "none";
    if (sources.size === 1) return Array.from(sources)[0];
    return Array.from(sources).sort().join("+");
};
const ProvenanceCaption = ({ buckets, hints, testid }) => {
    const probes = hints.flatMap((h) => buckets[h] || []);
    if (!probes.length) return null;
    const labels = Array.from(new Set(probes.map((p) => p.probe.label))).join(", ");
    const allRecords = probes.flatMap((p) => p.records || []);
    const src = sourceMixLabel(allRecords);
    const uniqueProbeCount = new Set(probes.map((p) => p.probe.id)).size;
    return (
        <p data-testid={testid} className="tc-mono text-[10px] text-[#5a6669] mt-2 tracking-wide">
            predicted: mock_fhp_v1 · monitored: {uniqueProbeCount} probe{uniqueProbeCount === 1 ? "" : "s"} ({labels}) · {src}
        </p>
    );
};

export default function ThermalTab() {
    const { project } = useOutletContext();
    const { tier, meets } = useTier();
    const [searchParams, setSearchParams] = useSearchParams();
    const [pours, setPours] = useState([]); const [mixes, setMixes] = useState([]); const [geoms, setGeoms] = useState([]); const [scenarios, setScenarios] = useState([]);
    const [selectedPour, setSelectedPour] = useState(searchParams.get("pour") || "");
    const [mode, setMode] = useState(meets("starter") ? "2d" : "1d");
    const [job, setJob] = useState(null);
    const [result, setResult] = useState(null);
    const [dtThreshold, setDtThreshold] = useState(20);
    const [contourFrame, setContourFrame] = useState(0);
    const pollRef = useRef(null);

    useEffect(() => {
        (async () => {
            const [p, m, g, s] = await Promise.all([
                projectApi.listPours(project.id), projectApi.listMixes(project.id),
                projectApi.listGeometries(project.id), projectApi.listScenarios(project.id),
            ]);
            setPours(p); setMixes(m); setGeoms(g); setScenarios(s);
            if (!selectedPour && p[0]) setSelectedPour(p[0].id);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [project.id]);

    useEffect(() => {
        if (!selectedPour) return;
        setSearchParams((sp) => { sp.set("pour", selectedPour); return sp; });
        (async () => {
            try {
                const list = await projectApi.listJobs({ project_id: project.id, pour_id: selectedPour });
                const latest = list[0] || null;
                setJob(latest);
                if (latest && latest.status === "succeeded" && latest.result_id) {
                    const r = await projectApi.getResult(latest.result_id);
                    setResult(r);
                } else { setResult(null); }
            } catch (e) { /* ignore */ }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPour]);

    useEffect(() => {
        if (!job || job.status === "succeeded" || job.status === "failed" || job.status === "cancelled") {
            if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
            return;
        }
        pollRef.current = setInterval(async () => {
            try {
                const fresh = await projectApi.getJob(job.id);
                setJob(fresh);
                if (fresh.status === "succeeded" && fresh.result_id) {
                    const r = await projectApi.getResult(fresh.result_id);
                    setResult(r);
                }
            } catch { /* ignore */ }
        }, 600);
        return () => { if (pollRef.current) clearInterval(pollRef.current); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [job?.id, job?.status]);

    const pour = pours.find((p) => p.id === selectedPour);
    const pourReady = pour && pour.mix_id && pour.geometry_id && pour.scenario_id;
    const running = job && (job.status === "queued" || job.status === "running");

    // ── Overlay state (Phase 2B-iii) ──
    const [overlayOn, setOverlayOn] = useState(searchParams.get("overlay") === "monitoring");
    const [monitoring, setMonitoring] = useState(null);       // { by_probe: [...] }
    const [monitoredEst, setMonitoredEst] = useState(null);   // StrengthEstimate
    const hasMonitoring = monitoring && monitoring.by_probe && monitoring.by_probe.some((b) => (b.records || []).length > 0);

    useEffect(() => {
        if (!selectedPour) return;
        (async () => {
            try {
                const m = await projectApi.getMonitoring(selectedPour);
                setMonitoring(m);
                const ests = await projectApi.listStrengthEstimates(selectedPour, "monitored");
                setMonitoredEst(ests[0] || null);
            } catch { setMonitoring(null); setMonitoredEst(null); }
        })();
    }, [selectedPour]);

    useEffect(() => {
        setSearchParams((sp) => {
            if (overlayOn) sp.set("overlay", "monitoring"); else sp.delete("overlay");
            return sp;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [overlayOn]);

    const cancelJob = async () => {
        if (!job) return;
        try { const fresh = await projectApi.cancelJob(job.id); setJob(fresh); toast.success("Job cancelled."); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    const runSolver = async () => {
        if (!pour) return;
        try {
            const { job_id } = await projectApi.runSolver({ pour_id: pour.id, mode: meets("starter") ? mode : "1d" });
            toast.success("Solver job queued.");
            const fresh = await projectApi.getJob(job_id);
            setJob(fresh); setResult(null);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            if (detail?.error === "pour_incomplete") {
                toast.error(`Pour is missing ${detail.missing.join(", ")}. Open Setup to relink.`);
            } else {
                toast.error(formatApiError(e));
            }
        }
    };

    if (pours.length === 0) {
        return <div data-testid="thermal-empty" className="border border-[#d8dcdd] bg-[#ffffff] p-7 text-[13px] text-[#5a6669]">No pours yet — create one in Setup before running thermal predictions. <Link to={`/app/projects/${project.id}/setup`} className="text-[#01696f] hover:text-[#015a60]">Go to Setup →</Link></div>;
    }

    const labelFor = (p) => {
        const m = mixes.find(x => x.id === p.mix_id); const g = geoms.find(x => x.id === p.geometry_id); const s = scenarios.find(x => x.id === p.scenario_id);
        return `#${p.sequence_index} · ${p.name} · ${m?.name || "no mix"} · ${g?.shape_type || "no geom"} · ${s?.name || "no sc"}`;
    };

    return (
        <div data-testid="thermal-tab" className="flex flex-col">
            <IntroStrip />
            {/* Run controls */}
            <Panel title="Run prediction" testid="thermal-run-controls" sub={tier === "demo" ? "demo · 1D forced" : `${tier} · ${mode.toUpperCase()}`}>
                <div className="mb-3 flex items-center gap-3 flex-wrap"><SolverBadge overlayActive={overlayOn && hasMonitoring} />
                    {hasMonitoring ? (
                        <label className="inline-flex items-center gap-1.5 text-[12px] cursor-pointer" data-testid="overlay-toggle">
                            <input type="checkbox" checked={overlayOn} onChange={(e) => setOverlayOn(e.target.checked)} className="accent-[#01696f]" />
                            Show monitoring overlay
                        </label>
                    ) : (
                        <span className="tc-mono text-[10.5px] text-[#5a6669]" data-testid="overlay-disabled">monitoring overlay — no data for this pour</span>
                    )}
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-end">
                    <div className="lg:col-span-7">
                        <label className="tc-mono text-[10px] text-[#5a6669] tracking-wider uppercase">Pour</label>
                        <select value={selectedPour} onChange={(e) => setSelectedPour(e.target.value)} data-testid="thermal-pour-selector"
                            className="w-full mt-1 bg-transparent border border-[#d8dcdd] focus:border-[#01696f] outline-none px-3 py-2 text-[13px] text-[#0e1416]">
                            {pours.map((p) => <option key={p.id} value={p.id}>{labelFor(p)}</option>)}
                        </select>
                    </div>
                    <div className="lg:col-span-3">
                        <label className="tc-mono text-[10px] text-[#5a6669] tracking-wider uppercase">Mode</label>
                        <div className="flex gap-3 mt-2">
                            <label className="inline-flex items-center gap-1.5 text-[12.5px] cursor-pointer"><input type="radio" checked={mode === "1d"} onChange={() => setMode("1d")} className="accent-[#01696f]" data-testid="thermal-mode-1d" />1D lumped</label>
                            <label className={`inline-flex items-center gap-1.5 text-[12.5px] ${meets("starter") ? "cursor-pointer" : "opacity-60"}`}><input type="radio" checked={mode === "2d"} onChange={() => meets("starter") && setMode("2d")} disabled={!meets("starter")} className="accent-[#01696f]" data-testid="thermal-mode-2d" />2D contour {!meets("starter") && <span className="tc-mono text-[10px] text-[#5a6669]">· Starter+</span>}</label>
                        </div>
                    </div>
                    <div className="lg:col-span-2 flex justify-end">
                        <button onClick={runSolver} disabled={!pourReady || running} data-testid="thermal-run-button"
                            className="inline-flex items-center gap-2 bg-[#01696f] hover:bg-[#015a60] disabled:opacity-60 text-[#ffffff] px-3.5 py-2 text-[12.5px] transition-colors">
                            <Play size={13} strokeWidth={1.5} />{running ? "Running…" : "Run prediction"}
                        </button>
                    </div>
                </div>
                {!pourReady && pour && (
                    <p className="mt-3 tc-mono text-[11px] text-[#5a6669]">Complete the pour&apos;s mix + geometry + scenario in <Link to={`/app/projects/${project.id}/setup`} className="text-[#01696f] hover:text-[#015a60]">Setup</Link> before running a prediction.</p>
                )}

                {job && (
                    <div className="mt-4 pt-4 border-t border-[#d8dcdd] flex items-center gap-4" data-testid="thermal-job-strip">
                        <span className="tc-mono text-[11px] text-[#5a6669]">{new Date(job.created_at).toLocaleString()}</span>
                        <span className={`tc-mono text-[10.5px] tracking-widest uppercase ${job.status === "succeeded" ? "text-[#01696f]" : job.status === "failed" ? "text-[#a13434]" : "text-[#5a6669]"}`} data-testid="thermal-job-status">● {job.status}</span>
                        {running && (
                            <div className="flex-1 max-w-[260px]">
                                <div className="h-[3px] bg-[#d8dcdd]"><div className="h-full bg-[#01696f] transition-all" style={{ width: `${Math.round((job.progress || 0) * 100)}%` }} /></div>
                                <span className="tc-mono text-[10px] text-[#5a6669] mt-1 inline-block">{Math.round((job.progress || 0) * 100)}% · {job.progress_label || "…"}</span>
                            </div>
                        )}
                        {job.status === "failed" && job.error && <span className="tc-mono text-[11px] text-[#a13434]">{job.error}</span>}
                        {running && (
                            <button onClick={cancelJob} data-testid="thermal-cancel-button"
                                    className="ml-auto tc-mono text-[10.5px] text-[#a13434] border border-[#a13434] hover:bg-[#a13434] hover:text-white px-2 py-1 transition-colors">Cancel job</button>
                        )}
                    </div>
                )}
            </Panel>

            {!result && !running && (
                <Panel title="No prediction yet" testid="thermal-empty-result"><p className="text-[13px] text-[#5a6669]">No prediction yet — run the solver to see thermal, maturity, and predicted strength results for this pour.</p></Panel>
            )}

            {result && <ResultDashboard project={project} pour={selectedPour ? { id: selectedPour } : null} result={result} tier={tier} meets={meets} dtThreshold={dtThreshold} setDtThreshold={setDtThreshold} contourFrame={contourFrame} setContourFrame={setContourFrame} overlayOn={overlayOn && hasMonitoring} monitoring={monitoring} monitoredEst={monitoredEst} />}
        </div>
    );
}

function ResultDashboard({ project, pour, result, tier, meets, dtThreshold, setDtThreshold, contourFrame, setContourFrame, overlayOn, monitoring, monitoredEst }) {
    const t = result.time_axis_h;
    const W = 720, H = 200, pad = { l: 36, r: 14, t: 12, b: 26 };
    const xMax = t[t.length - 1] || 72;
    const xS = (v) => pad.l + (v / xMax) * (W - pad.l - pad.r);
    const mkY = (arr, yMin, yMax) => (v) => H - pad.b - ((v - yMin) / Math.max(0.001, yMax - yMin)) * (H - pad.t - pad.b);

    const pathOf = (ys, yS) => `M ${ys.map((y, i) => `${xS(t[i])} ${yS(y)}`).join(" L ")}`;
    const axes = (yS, yMin, yMax) => (
        <g>
            <line x1={pad.l} y1={H - pad.b} x2={W - pad.r} y2={H - pad.b} stroke="#0e1416" strokeWidth="0.7" />
            <line x1={pad.l} y1={pad.t} x2={pad.l} y2={H - pad.b} stroke="#0e1416" strokeWidth="0.7" />
            {[0, 0.25, 0.5, 0.75, 1].map((f, i) => {
                const v = yMin + (yMax - yMin) * f; const y = yS(v);
                return <g key={i}><line x1={pad.l - 3} y1={y} x2={pad.l} y2={y} stroke="#5a6669" strokeWidth="0.5" /><text x={pad.l - 6} y={y + 3} textAnchor="end" fontSize="8" fill="#5a6669" fontFamily="JetBrains Mono">{v.toFixed(v < 1 ? 2 : 0)}</text></g>;
            })}
            {[0, 6, 12, 24, 36, 48, 60, 72].filter(v => v <= xMax).map((v) => <g key={v}><line x1={xS(v)} y1={H - pad.b} x2={xS(v)} y2={H - pad.b + 3} stroke="#5a6669" strokeWidth="0.5" /><text x={xS(v)} y={H - pad.b + 14} textAnchor="middle" fontSize="10" fill="#0e1416" fontFamily="JetBrains Mono">{v} h</text></g>)}
        </g>
    );

    // ── Phase 2E · shared-hover wrapper for time-history charts ──
    // Renders the chart `children` (SVG content) + an invisible mouse-capture
    // rect inside the SVG + a vertical crosshair + an HTML tooltip card with
    // all series values at the hovered time index. Same UX across every
    // time-history chart in this module.
    function TimeChart({ series, yS, yMin, yMax, children, extraRows = null }) {
        const wrapRef = useRef(null);
        const [hoverIdx, setHoverIdx] = useState(null);
        const onMove = (e) => {
            const svgEl = e.currentTarget.querySelector("svg");
            if (!svgEl) return;
            const rect = svgEl.getBoundingClientRect();
            const pxPerUnit = rect.width / W;
            const viewX = (e.clientX - rect.left) / pxPerUnit;
            if (viewX < pad.l || viewX > W - pad.r) { setHoverIdx(null); return; }
            const tHover = ((viewX - pad.l) / (W - pad.l - pad.r)) * xMax;
            // closest index in t[]
            let best = 0, bestD = Infinity;
            for (let i = 0; i < t.length; i++) {
                const d = Math.abs(t[i] - tHover);
                if (d < bestD) { bestD = d; best = i; }
            }
            setHoverIdx(best);
        };
        const onLeave = () => setHoverIdx(null);

        const hoverX = hoverIdx != null ? xS(t[hoverIdx]) : null;
        // Tooltip position in % of chart width — keep inside bounds
        const tooltipLeftPct = hoverX != null ? Math.min(82, Math.max(6, (hoverX / W) * 100)) : 0;

        return (
            <div ref={wrapRef} onMouseMove={onMove} onMouseLeave={onLeave} className="relative" data-testid="chart-hover-wrap">
                <svg viewBox={`0 0 ${W} ${H}`} width="100%">
                    {children}
                    {hoverIdx != null && (
                        <g data-testid="chart-hover-crosshair" pointerEvents="none">
                            <line x1={hoverX} y1={pad.t} x2={hoverX} y2={H - pad.b} stroke="#0e1416" strokeWidth="0.6" strokeDasharray="2 2" />
                            {series.map((s, i) => {
                                const v = s.values?.[hoverIdx];
                                if (v == null || Number.isNaN(v)) return null;
                                return <circle key={i} cx={hoverX} cy={yS(v)} r="2.5" fill={s.color} />;
                            })}
                        </g>
                    )}
                </svg>
                {hoverIdx != null && (
                    <div
                        data-testid="chart-hover-tooltip"
                        className="absolute -top-1 tc-mono text-[10.5px] bg-[#0e1416] text-[#e6ebec] border border-[#1a2124] px-2.5 py-2 leading-[1.45] pointer-events-none shadow-lg"
                        style={{ left: `${tooltipLeftPct}%`, transform: "translateY(-100%)", minWidth: "120px" }}
                    >
                        <div className="text-[#01696f] mb-1">{Math.round(t[hoverIdx])} h</div>
                        {series.map((s, i) => {
                            const v = s.values?.[hoverIdx];
                            if (v == null || Number.isNaN(v)) return null;
                            return (
                                <div key={i} className="flex items-center justify-between gap-3">
                                    <span className="flex items-center gap-1.5"><span className="inline-block w-2 h-px" style={{ background: s.color }} />{s.name}</span>
                                    <span>{Number(v).toFixed(s.decimals ?? 1)}{s.unit ? ` ${s.unit}` : ""}</span>
                                </div>
                            );
                        })}
                        {extraRows && extraRows(hoverIdx)}
                    </div>
                )}
            </div>
        );
    }

    const ms = result.milestones || {};
    return (
        <>
            {/* ── PILLAR A ─ Thermal performance & cracking risk (Panels 1–5) ── */}
            <PillarHeader index="A" title="Thermal performance & cracking risk" testid="pillar-a-header" />

            {/* Panel 1 — Contour (Starter+, only when 2D field is present) */}
            {result.mode_used === "2d" && result.temperature_field && (
                <Panel title="Temperature field · 2D contour" sub={`frame ${contourFrame + 1}/${result.temperature_field.frames.length} · t = ${result.temperature_field.frames_t_h[contourFrame]} h`} testid="panel-contour">
                    <ContourFrame frame={result.temperature_field.frames[contourFrame]} />
                    <input type="range" min="0" max={result.temperature_field.frames.length - 1} value={contourFrame} onChange={(e) => setContourFrame(parseInt(e.target.value))} data-testid="contour-time-slider" className="w-full mt-3 accent-[#01696f]" />
                </Panel>
            )}

            {/* Panel 2 — Core/mid/surface */}
            <Panel title={overlayOn ? "Temperature history · core, mid, surface · predicted + monitored" : "Temperature history · core, mid, surface"} sub={`peak core ${ms.peak_core_temp_C} °C at ${ms.peak_core_at_h} h`} testid="panel-temp-history">
                {(() => {
                    const all = [...result.core_history, ...result.mid_history, ...result.surface_history];
                    const monPts = overlayOn && monitoring ? (monitoring.by_probe || []).flatMap((b) => (b.records || []).flatMap((r) => (r.samples || []).map((s) => s.temperature_C))) : [];
                    const yMin = Math.floor(Math.min(...all, ...monPts) - 2); const yMax = Math.ceil(Math.max(...all, ...monPts) + 2);
                    const yS = mkY(null, yMin, yMax);
                    const monStroke = { core: "#01696f", mid: "#2f3a3d", surface: "#8a9498", other: "#5a6669" };
                    return (
                        <TimeChart yS={yS} yMin={yMin} yMax={yMax} series={[
                            { name: "Core",    values: result.core_history,    color: "#01696f", unit: "°C", decimals: 1 },
                            { name: "Mid",     values: result.mid_history,     color: "#2f3a3d", unit: "°C", decimals: 1 },
                            { name: "Surface", values: result.surface_history, color: "#8a9498", unit: "°C", decimals: 1 },
                        ]}>
                            {axes(yS, yMin, yMax)}
                            <path d={pathOf(result.core_history, yS)} fill="none" stroke="#01696f" strokeWidth="1.5" />
                            <path d={pathOf(result.mid_history, yS)} fill="none" stroke="#2f3a3d" strokeWidth="1.2" />
                            <path d={pathOf(result.surface_history, yS)} fill="none" stroke="#8a9498" strokeWidth="1.2" strokeDasharray="3 3" />
                            <line x1={xS(ms.peak_core_at_h)} y1={pad.t} x2={xS(ms.peak_core_at_h)} y2={H - pad.b} stroke="#01696f" strokeWidth="0.6" strokeDasharray="2 2" />
                            {overlayOn && monitoring && (monitoring.by_probe || []).flatMap((b) => (b.records || []).map((r, ri) => {
                                if (!b.probe) return null;
                                const samples = (r.samples || []).filter((s) => s.t_hours_from_placement <= xMax + 1);
                                if (samples.length < 2) return null;
                                const stroke = monStroke[b.probe.location_hint] || monStroke.other;
                                const d = `M ${samples.map((s) => `${xS(Math.min(xMax, s.t_hours_from_placement))} ${yS(s.temperature_C)}`).join(" L ")}`;
                                const uploaded = r.uploaded_at ? new Date(r.uploaded_at).toLocaleString() : "—";
                                return <path key={`${b.probe.id}-${ri}`} d={d} fill="none" stroke={stroke} strokeWidth="1.4" strokeDasharray="5 3" data-testid={`monitored-series-${b.probe.label}-${b.probe.location_hint}`}><title>{`Probe ${b.probe.label} · ${b.probe.location_hint} · source=${r.source} · probe_id=${b.probe.id} · uploaded_at=${uploaded}`}</title></path>;
                            }))}
                        </TimeChart>
                    );
                })()}
                <div className="flex flex-wrap gap-4 mt-2 tc-mono text-[10.5px] text-[#5a6669]">
                    <span><span className="inline-block w-3 h-px bg-[#01696f] align-middle" /> core (predicted)</span>
                    <span><span className="inline-block w-3 h-px bg-[#2f3a3d] align-middle" /> mid (predicted)</span>
                    <span><span className="inline-block w-3 h-px border-t border-dashed border-[#8a9498] align-middle" /> surface (predicted)</span>
                    {overlayOn && monitoring && (monitoring.by_probe || []).filter((b) => b.probe && (b.records || []).length).map((b) => (
                        <span key={b.probe.id}><span className="inline-block w-3 h-px border-t border-dashed align-middle" style={{ borderColor: ({core:"#01696f", mid:"#2f3a3d", surface:"#8a9498", other:"#5a6669"})[b.probe.location_hint] || "#5a6669" }} /> Probe {b.probe.label} · {b.probe.location_hint} (monitored)</span>
                    ))}
                </div>
                {overlayOn && (
                    <ProvenanceCaption buckets={probeBuckets(monitoring)} hints={["core","mid","surface","other"]} testid="panel-temp-history-provenance" />
                )}
            </Panel>

            {/* Panel 3 — ΔT */}
            <Panel title={overlayOn ? "Core-to-surface ΔT · predicted + monitored" : "Core-to-surface ΔT"} sub={`peak ΔT ${ms.peak_dt_C} °C at ${ms.peak_dt_at_h} h`} testid="panel-dt">
                <div className="flex items-center gap-3 mb-3"><label className="tc-mono text-[10px] text-[#5a6669] tracking-wider uppercase">Threshold (°C)</label>
                    <input type="number" step="1" value={dtThreshold} onChange={(e) => setDtThreshold(parseFloat(e.target.value) || 0)} data-testid="dt-threshold-input" className="w-20 bg-transparent border-b border-[#d8dcdd] outline-none px-0 py-1 text-[13px]" /></div>
                {(() => {
                    const ys = result.delta_t_history;
                    const buckets = overlayOn ? probeBuckets(monitoring) : { core: [], surface: [], mid: [], other: [] };
                    const mon = overlayOn ? computeMonitoredDeltaT(t, buckets) : { series: [], peak: null };
                    const monVals = mon.series.filter((p) => p != null).map((p) => p.dT);
                    const yMin = 0; const yMax = Math.ceil(Math.max(...ys, dtThreshold + 5, ...(monVals.length ? monVals : [0])));
                    const yS = mkY(null, yMin, yMax);
                    return (
                        <>
                            <TimeChart yS={yS} yMin={yMin} yMax={yMax} series={[
                                { name: "ΔT predicted", values: ys, color: "#01696f", unit: "°C", decimals: 1 },
                                ...(overlayOn && mon.series.length ? [{
                                    name: "ΔT monitored",
                                    values: mon.series.map((p) => p == null ? null : p.dT),
                                    color: "#01696f", unit: "°C", decimals: 1,
                                }] : []),
                            ]}>
                                {axes(yS, yMin, yMax)}
                                <line x1={pad.l} y1={yS(dtThreshold)} x2={W - pad.r} y2={yS(dtThreshold)} stroke="#a13434" strokeWidth="0.7" strokeDasharray="3 3" />
                                <path d={pathOf(ys, yS)} fill="none" stroke="#01696f" strokeWidth="1.5" />
                                <path d={`M ${pad.l} ${yS(dtThreshold)} L ${ys.map((y, i) => `${xS(t[i])} ${yS(Math.max(y, dtThreshold))}`).join(" L ")} L ${W - pad.r} ${yS(dtThreshold)} Z`} fill="rgba(161,52,52,0.10)" />
                                {overlayOn && mon.series.length > 0 && (() => {
                                    const validPts = mon.series.map((p) => p == null ? null : `${xS(p.t)},${yS(Math.max(yMin, Math.min(yMax, p.dT)))}`);
                                    const segs = []; let cur = [];
                                    for (const s of validPts) { if (s == null) { if (cur.length > 1) segs.push(cur); cur = []; } else cur.push(s); }
                                    if (cur.length > 1) segs.push(cur);
                                    return segs.map((seg, k) => <path key={k} d={`M ${seg.join(" L ")}`} fill="none" stroke="#01696f" strokeWidth="1.4" strokeDasharray="5 3" data-testid={`monitored-dt-segment-${k}`}><title>Monitored ΔT · cores: {buckets.core.map(p=>p.probe.label).join(", ")} ↔ surfaces: {buckets.surface.map(p=>p.probe.label).join(", ")}</title></path>);
                                })()}
                            </TimeChart>
                            <div className="flex flex-wrap gap-4 mt-2 tc-mono text-[10.5px] text-[#5a6669]">
                                <span><span className="inline-block w-3 h-px bg-[#01696f] align-middle" /> ΔT (predicted)</span>
                                {overlayOn && buckets.core.length > 0 && buckets.surface.length > 0 && (
                                    <span><span className="inline-block w-3 h-px border-t border-dashed border-[#01696f] align-middle" /> Monitored ΔT · probes {buckets.core.map(p=>p.probe.label).join(", ")} ↔ {buckets.surface.map(p=>p.probe.label).join(", ")}</span>
                                )}
                            </div>
                            {overlayOn && mon.peak && (
                                <p data-testid="monitored-dt-peak" className="tc-mono text-[11px] text-[#0e1416] mt-2">Monitored peak ΔT: {mon.peak.dT.toFixed(2)} °C at {mon.peak.t.toFixed(1)} h</p>
                            )}
                            {overlayOn && !(buckets.core.length && buckets.surface.length) && (
                                <p className="tc-mono text-[10.5px] text-[#5a6669] mt-2" data-testid="monitored-dt-skip">Monitored ΔT requires ≥1 core probe and ≥1 surface probe — none of one type for this pour.</p>
                            )}
                            {overlayOn && buckets.core.length > 0 && buckets.surface.length > 0 && (
                                <ProvenanceCaption buckets={buckets} hints={["core","surface"]} testid="panel-dt-provenance" />
                            )}
                        </>
                    );
                })()}
            </Panel>

            {/* Panel 4 — σ_t vs f_t */}
            <Panel title="Tensile stress vs strength" testid="panel-stress">
                {(() => {
                    const all = [...result.sigma_t_history, ...result.ft_history]; const yMin = 0; const yMax = Math.ceil(Math.max(...all) + 0.5);
                    const yS = mkY(null, yMin, yMax);
                    return (
                        <TimeChart yS={yS} yMin={yMin} yMax={yMax} series={[
                            { name: "σ_t",  values: result.sigma_t_history, color: "#01696f", unit: "MPa", decimals: 2 },
                            { name: "f_t",  values: result.ft_history,      color: "#2f3a3d", unit: "MPa", decimals: 2 },
                        ]}>
                            {axes(yS, yMin, yMax)}
                            <path d={pathOf(result.ft_history, yS)} fill="none" stroke="#2f3a3d" strokeWidth="1.2" strokeDasharray="3 3" />
                            <path d={pathOf(result.sigma_t_history, yS)} fill="none" stroke="#01696f" strokeWidth="1.5" />
                        </TimeChart>
                    );
                })()}
                <div className="flex gap-5 mt-2 tc-mono text-[10.5px] text-[#5a6669]"><span><span className="inline-block w-3 h-px bg-[#01696f] align-middle" /> σ_t</span><span><span className="inline-block w-3 h-px border-t border-dashed border-[#2f3a3d] align-middle" /> f_t</span></div>
                {overlayOn && <p className="tc-mono text-[10.5px] text-[#5a6669] mt-3" data-testid="stress-deferred-note">Monitored stress / CIF overlays require mechanical re-solve — deferred (Phase 2B-iv).</p>}
            </Panel>

            {/* Panel 5 — CIF */}
            {(() => { const cifBand = CIF_BANDS.find((b) => b.test(ms.max_cif)); return (
            <Panel title="Cracking index factor (CIF)" sub={`max CIF ${ms.max_cif} at ${ms.max_cif_at_h} h — ${cifBand.label}`} testid="panel-cif">
                {(() => {
                    const ys = result.cif_history; const yMax = Math.max(1.2, ...ys, 1.0); const yMin = 0;
                    const yS = mkY(null, yMin, yMax);
                    return (
                        <TimeChart yS={yS} yMin={yMin} yMax={yMax} series={[
                            { name: "CIF", values: ys, color: "#01696f", unit: "", decimals: 2 },
                        ]}>
                            {axes(yS, yMin, yMax)}
                            <line x1={pad.l} y1={yS(1.0)} x2={W - pad.r} y2={yS(1.0)} stroke="#a13434" strokeWidth="0.7" />
                            <path d={pathOf(ys, yS)} fill="none" stroke="#01696f" strokeWidth="1.5" />
                        </TimeChart>
                    );
                })()}
                <div data-testid={cifBand.testid} className={`mt-4 border ${toneClasses[cifBand.tone]} px-3 py-2.5`}>
                    <div className="tc-mono text-[10.5px] tracking-widest uppercase">Interpretation · max CIF {ms.max_cif?.toFixed?.(2) ?? ms.max_cif}</div>
                    <div className="tc-mono text-[12.5px] mt-1 font-medium">{cifBand.label}</div>
                    <div className="text-[12px] text-[#0e1416] mt-1 leading-snug">{cifBand.body}</div>
                </div>
                {overlayOn && <p className="tc-mono text-[10.5px] text-[#5a6669] mt-3" data-testid="cif-deferred-note">Monitored stress / CIF overlays require mechanical re-solve — deferred (Phase 2B-iv).</p>}
            </Panel>
            ); })()}

            {/* ── PILLAR B ─ Maturity-based strength estimation (Panels 6–7) ── */}
            <PillarHeader index="B" title="Maturity-based strength estimation" testid="pillar-b-header" />
            <div data-testid="pillar-b-intro" className="border border-[#d8dcdd] bg-[#f6f7f6] px-5 py-3 mb-5">
                <p className="text-[12.5px] text-[#0e1416] leading-relaxed">
                    Strength is <span className="tc-mono text-[#01696f]">not</span> read directly from temperature{overlayOn ? " — predicted and monitored alike" : ""}. {overlayOn
                        ? <>Both apply the same maturity method
                            {result.inputs_snapshot?.maturity_method ? <> (<span className="tc-mono">{result.inputs_snapshot.maturity_method}</span>)</> : null} and this mix&apos;s calibrated curve to their respective temperature histories.</>
                        : <>The temperature-time history is converted to <span className="tc-mono text-[#0e1416]">equivalent age</span> via the project&apos;s maturity method
                            {result.inputs_snapshot?.maturity_method ? <> (<span className="tc-mono">{result.inputs_snapshot.maturity_method}</span>)</> : null}, and equivalent age is then
                            mapped to <span className="tc-mono text-[#0e1416]">predicted in-place strength</span> through the mix-specific calibration fit.</>}
                </p>
            </div>

            {/* Panel 6 — equivalent age */}
            {(() => {
                const matMethod = result.inputs_snapshot?.maturity_method || "nurse_saul";
                const matParams = result.inputs_snapshot?.maturity_params || {};
                const buckets = overlayOn ? probeBuckets(monitoring) : { core: [], surface: [], mid: [], other: [] };
                const mon = overlayOn ? computeMonitoredTe(t, buckets, matMethod, matParams) : { series: [], peak: null };
                const titleSuffix = matMethod === "arrhenius"
                    ? `method: Arrhenius E_a=${matParams.E_a ?? 33500}`
                    : `method: Nurse-Saul T₀=${matParams.datum_temp ?? -10}`;
                return (
                    <Panel
                        title={overlayOn ? `Equivalent age tₑ · predicted + monitored · ${titleSuffix}` : `Equivalent age tₑ · method ${matMethod}`}
                        sub="step 1 of 2 · temperature → equivalent age"
                        testid="panel-te">
                        {(() => {
                            const ys = result.equivalent_age_history;
                            const monVals = mon.series.filter((p) => p != null).map((p) => p.te);
                            const yMin = 0; const yMax = Math.ceil(Math.max(...ys, ...(monVals.length ? monVals : [0])) * 1.05);
                            const yS = mkY(null, yMin, yMax);
                            return (
                                <svg viewBox={`0 0 ${W} ${H}`} width="100%">{axes(yS, yMin, yMax)}
                                    <path d={pathOf(ys, yS)} fill="none" stroke="#01696f" strokeWidth="1.5" />
                                    {overlayOn && mon.series.length > 0 && (() => {
                                        const validPts = mon.series.map((p) => p == null ? null : `${xS(p.t)},${yS(Math.max(yMin, Math.min(yMax, p.te)))}`);
                                        const segs = []; let cur = [];
                                        for (const s of validPts) { if (s == null) { if (cur.length > 1) segs.push(cur); cur = []; } else cur.push(s); }
                                        if (cur.length > 1) segs.push(cur);
                                        return segs.map((seg, k) => <path key={k} d={`M ${seg.join(" L ")}`} fill="none" stroke="#01696f" strokeWidth="1.4" strokeDasharray="5 3" data-testid={`monitored-te-segment-${k}`}><title>Monitored tₑ · cores: {buckets.core.map(p=>p.probe.label).join(", ")}</title></path>);
                                    })()}
                                </svg>
                            );
                        })()}
                        <div className="flex flex-wrap gap-4 mt-2 tc-mono text-[10.5px] text-[#5a6669]">
                            <span><span className="inline-block w-3 h-px bg-[#01696f] align-middle" /> tₑ (predicted)</span>
                            {overlayOn && buckets.core.length > 0 && (
                                <span><span className="inline-block w-3 h-px border-t border-dashed border-[#01696f] align-middle" /> Monitored tₑ · probes {buckets.core.map(p=>p.probe.label).join(", ")}</span>
                            )}
                        </div>
                        {overlayOn && !buckets.core.length && (
                            <p className="tc-mono text-[10.5px] text-[#5a6669] mt-2" data-testid="monitored-te-skip">Monitored tₑ requires ≥1 core probe — none flagged for this pour.</p>
                        )}
                        {overlayOn && buckets.core.length > 0 && (
                            <ProvenanceCaption buckets={buckets} hints={["core"]} testid="panel-te-provenance" />
                        )}
                    </Panel>
                );
            })()}

            {/* Panel 7 — predicted strength */}
            <Panel
                title={overlayOn && monitoredEst ? "In-place strength · predicted + monitored" : "Predicted in-place strength f_c"}
                sub="step 2 of 2 · equivalent age → calibrated strength"
                testid="panel-fc">
                {(() => {
                    const ys = result.predicted_strength_history; const yMin = 0; const yMax = Math.ceil(Math.max(...ys) * 1.05);
                    const yS = mkY(null, yMin, yMax);
                    const fc_inf = Math.max(...ys);
                    return (
                        <svg viewBox={`0 0 ${W} ${H}`} width="100%">{axes(yS, yMin, yMax)}
                            <line x1={pad.l} y1={yS(0.4 * fc_inf)} x2={W - pad.r} y2={yS(0.4 * fc_inf)} stroke="#5a6669" strokeWidth="0.5" strokeDasharray="2 2" />
                            <line x1={pad.l} y1={yS(0.7 * fc_inf)} x2={W - pad.r} y2={yS(0.7 * fc_inf)} stroke="#5a6669" strokeWidth="0.5" strokeDasharray="2 2" />
                            <text x={W - pad.r} y={yS(0.4 * fc_inf) - 3} textAnchor="end" fontSize="8" fill="#5a6669" fontFamily="JetBrains Mono">formwork release (0.4·fc∞)</text>
                            <text x={W - pad.r} y={yS(0.7 * fc_inf) - 3} textAnchor="end" fontSize="8" fill="#5a6669" fontFamily="JetBrains Mono">PT window (0.7·fc∞)</text>
                            <path d={pathOf(ys, yS)} fill="none" stroke="#01696f" strokeWidth="1.6" />
                            {overlayOn && monitoredEst && monitoredEst.history && monitoredEst.history.length > 1 && (() => {
                                const pts = monitoredEst.history.filter((p) => p.t <= xMax);
                                if (pts.length < 2) return null;
                                const d = `M ${pts.map((p) => `${xS(Math.min(xMax, p.t))} ${yS(Math.max(yMin, Math.min(yMax, p.fc_est)))}`).join(" L ")}`;
                                const probeIds = (monitoredEst.inputs_snapshot || {}).probe_ids_used || [];
                                return <path d={d} fill="none" stroke="#01696f" strokeWidth="1.6" strokeDasharray="5 3" data-testid="monitored-fc-series"><title>{`Monitored fc · source=${monitoredEst.source} · probes=${probeIds.length} · computed_at=${new Date(monitoredEst.computed_at).toLocaleString()}`}</title></path>;
                            })()}
                        </svg>
                    );
                })()}
                <div className="grid grid-cols-2 gap-3 mt-3">
                    <div className="border border-[#d8dcdd] bg-[#f6f7f6] p-3"><div className="tc-mono text-[10px] text-[#5a6669] tracking-widest uppercase">Predicted · Formwork release</div><div className="tc-mono text-[15px] text-[#0e1416] mt-1" data-testid="ms-formwork">{ms.formwork_release_at_h != null ? `${ms.formwork_release_at_h} h` : "not reached within 72 h"}</div></div>
                    <div className="border border-[#d8dcdd] bg-[#f6f7f6] p-3"><div className="tc-mono text-[10px] text-[#5a6669] tracking-widest uppercase">Predicted · PT window</div><div className="tc-mono text-[15px] text-[#0e1416] mt-1" data-testid="ms-pt">{ms.pt_window_at_h != null ? `${ms.pt_window_at_h} h` : "not reached"}</div></div>
                    {overlayOn && monitoredEst && (
                        <>
                            <div className="border border-[#01696f] bg-[rgba(1,105,111,0.04)] p-3"><div className="tc-mono text-[10px] text-[#01696f] tracking-widest uppercase">Monitored · Formwork release</div><div className="tc-mono text-[15px] text-[#0e1416] mt-1" data-testid="ms-formwork-monitored">{monitoredEst.milestones?.formwork_release_at_h != null ? `${monitoredEst.milestones.formwork_release_at_h} h` : "not reached yet"}</div></div>
                            <div className="border border-[#01696f] bg-[rgba(1,105,111,0.04)] p-3"><div className="tc-mono text-[10px] text-[#01696f] tracking-widest uppercase">Monitored · PT window</div><div className="tc-mono text-[15px] text-[#0e1416] mt-1" data-testid="ms-pt-monitored">{monitoredEst.milestones?.pt_window_at_h != null ? `${monitoredEst.milestones.pt_window_at_h} h` : "not reached yet"}</div></div>
                        </>
                    )}
                </div>
                {overlayOn && monitoredEst && (
                    <ProvenanceCaption buckets={probeBuckets(monitoring)} hints={["core"]} testid="panel-fc-provenance" />
                )}
            </Panel>

            {/* 2C-i.5b · live yield widgets (Pro-gated; visible when monitoring overlay is on AND a monitored estimate exists) */}
            {overlayOn && monitoredEst && project?.id && (
                <LiveYieldPanel
                    pour={pour}
                    monitoredEstimate={monitoredEst}
                    projectId={project.id}
                />
            )}

            <div className="mt-2 px-1 flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-2">
                <Mono className="text-[10.5px] text-[#5a6669]">Solver: {result.solver} · Job {result.job_id} · Computed {new Date(result.computed_at).toLocaleString()}</Mono>
                <span className="tc-mono text-[10.5px] text-[#5a6669] flex items-center gap-1.5"><Info size={11} strokeWidth={1.5} />Synthetic transient via lumped-node Freiesleben-Hansen & Pedersen model. Code_Aster integration deferred — solver contract preserved for swap-in.</span>
            </div>
        </>
    );
}

function ContourFrame({ frame }) {
    if (!frame) return null;
    const flat = frame.flat(); const tmin = Math.min(...flat); const tmax = Math.max(...flat);
    const span = Math.max(0.1, tmax - tmin);
    const cell = 14; const N = frame.length;
    return (
        <div className="flex gap-4">
            <svg viewBox={`0 0 ${N * cell} ${N * cell}`} width={N * cell} height={N * cell} className="border border-[#d8dcdd]" data-testid="contour-svg">
                {frame.map((row, i) => row.map((v, j) => {
                    const f = (v - tmin) / span; const r = Math.round(1 - f) * 0; const g = Math.round((1 - f) * 105 + f * 105); const b = Math.round((1 - f) * 111 + f * 111);
                    const alpha = 0.15 + f * 0.85;
                    return <rect key={`${i}-${j}`} x={j * cell} y={i * cell} width={cell} height={cell} fill={`rgba(1,105,111,${alpha.toFixed(2)})`} />;
                }))}
            </svg>
            <div className="flex flex-col justify-between tc-mono text-[10px] text-[#5a6669]">
                <span>{tmax.toFixed(1)} °C</span>
                <div className="flex-1 w-3 bg-gradient-to-b from-[#01696f] to-[rgba(1,105,111,0.15)] my-1" />
                <span>{tmin.toFixed(1)} °C</span>
            </div>
        </div>
    );
}
